import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  admin,
  type AdminClub,
  type AdminConsent,
  type AdminEnrollment,
  type AdminQuestionnaireSummary,
  type AdminUserDetail as UserDetailDto,
} from '../../api/admin'
import { ApiError } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'

interface QuestionnaireAccessRow extends AdminQuestionnaireSummary {
  revoked: boolean
  responded: boolean
}

/**
 * Detail page for `/admin/users/{id}`. Shows the user's basic info, a per-
 * role toggle (admin / tester), activity stats, recent sessions, and the
 * per-questionnaire revocation toggle that mirrors the Jinja2 surface we
 * deleted. Per-user revocation state is derived from "is this code in the
 * catalog AND no response from this user, but we know the admin revoked
 * it?" — we keep it simple by calling the catalog twice (admin global +
 * user-specific) and folding.
 */
export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const id = Number(userId)
  const { user: currentUser, refresh } = useAuth()
  const [data, setData] = useState<UserDetailDto | null>(null)
  const [catalog, setCatalog] = useState<AdminQuestionnaireSummary[] | null>(null)
  const [clubs, setClubs] = useState<AdminClub[]>([])
  const [clubSelection, setClubSelection] = useState<string>('')
  const [consents, setConsents] = useState<AdminConsent[]>([])
  const [enrollment, setEnrollment] = useState<AdminEnrollment[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [impersonating, setImpersonating] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [d, c, cl, co, en] = await Promise.all([
        admin.user(id),
        admin.questionnaires(),
        admin.clubs(),
        admin.consents(id),
        admin.enrollment(id),
      ])
      setData(d)
      setCatalog(c.items)
      setClubs(cl.clubs)
      setClubSelection(d.profile?.club ? String(d.profile.club.id) : '')
      setConsents(co)
      setEnrollment(en)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }, [id])

  useEffect(() => {
    if (Number.isFinite(id)) void load()
  }, [id, load])

  if (error) return <div className="banner-err">{error}</div>
  if (!data || !catalog) return <p className="text-sm text-text-muted">Cargando…</p>

  // We don't have a "revoked codes per user" endpoint yet; the backend's
  // detail JSON doesn't expose it (only the legacy Jinja2 view did). Until
  // we ship one, the toggle is purely intent-based: clicking "Quitar" calls
  // the revoke endpoint, "Restaurar" calls restore. We surface only the
  // *resulting* state inline (toast + count refresh) instead of a stateful
  // badge — a follow-up commit can add the explicit `revoked_codes` field
  // to GET /admin/users/{id}.
  const responded = new Set(
    // We don't have a list of responded codes per user in detail JSON; the
    // sessions list isn't questionnaires either. Leave the responded badge
    // for a future field.
    [] as string[],
  )
  const accessRows: QuestionnaireAccessRow[] = catalog.map((c) => ({
    ...c,
    revoked: false, // unknown without server flag — see comment above
    responded: responded.has(c.code),
  }))

  async function toggleRole(role: 'tester') {
    if (!data) return
    const has = data.user.roles.includes(role)
    setBusy(true)
    try {
      await admin.toggleAccess(id, role, has ? 'revoke' : 'grant')
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleQuestionnaire(code: string, action: 'revoke' | 'restore') {
    setBusy(true)
    try {
      await admin.toggleQuestionnaireAccess(id, code, action)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  async function saveClub() {
    setBusy(true)
    try {
      await admin.updateUser(id, {
        club_id: clubSelection === '' ? null : Number(clubSelection),
      })
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  async function recordConsent(isParental: boolean) {
    setBusy(true)
    try {
      await admin.recordConsent(id, { is_parental: isParental })
      setConsents(await admin.consents(id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  async function revokeConsent(consentId: number) {
    setBusy(true)
    try {
      await admin.revokeConsent(id, consentId)
      setConsents(await admin.consents(id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  async function handleImpersonate() {
    setImpersonating(true)
    setError(null)
    try {
      await admin.impersonate(id)
      await refresh()
      window.location.href = '/'
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al impersonar usuario')
      setImpersonating(false)
    }
  }

  async function setInclusion(meets: boolean) {
    setBusy(true)
    try {
      await admin.setEnrollment(id, { meets_inclusion: meets })
      setEnrollment(await admin.enrollment(id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setBusy(false)
    }
  }

  const u = data.user

  return (
    <section>
      <p>
        <Link to="/admin/users" className="text-sm text-text-muted">
          ← Usuarios
        </Link>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-md">
        <h1>
          {u.username}{' '}
          {u.is_superuser && <span className="tag-ok ml-1">Administrador</span>}
          {!u.is_active && <span className="tag-err ml-1">inactivo</span>}
        </h1>
        <div className="flex flex-wrap items-center gap-sm">
          {!u.is_superuser && currentUser?.user_id !== u.id && (
            <button
              type="button"
              className="btn-ghost"
              disabled={impersonating}
              onClick={() => void handleImpersonate()}
            >
              {impersonating ? 'Entrando…' : 'Impersonar'}
            </button>
          )}
          <Link to={`/admin/athletes/${id}`} className="btn no-underline">
            Ver evolución →
          </Link>
          <div className="flex flex-wrap items-center gap-sm text-sm">
            <Link to={`/admin/athletes/${id}/report`} className="btn no-underline">Ver informe</Link>
            <a
              href={admin.athleteReportPdfUrl(id, 'coach')}
              target="_blank"
              rel="noreferrer"
              className="btn no-underline"
            >
              Informe PDF
            </a>
          </div>
        </div>
      </div>

      <h2>Acceso a la demo</h2>
      <p className="text-sm text-text-muted">
        El acceso de prueba permite entrar a VR sin pertenecer a un club. No concede permisos de administración.
      </p>
      <div className="mb-lg flex flex-wrap gap-md">
        {data.manageable_access_roles.map((r) => {
          const has = u.roles.includes(r)
          return (
            <button
              key={r}
              type="button"
              disabled={busy || u.is_superuser}
              onClick={() => void toggleRole(r as 'tester')}
              className="btn-ghost flex items-center gap-sm"
            >
              <span className={has ? 'tag-ok' : 'tag'}>Acceso de prueba</span>
              <span className="text-xs">
                {u.is_superuser ? 'Incluido para administradores' : has ? 'Desactivar' : 'Activar'}
              </span>
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-2 gap-md sm:grid-cols-3">
        <StatCard label="Sesiones" value={data.activity.n_sessions} />
        <StatCard label="Trials" value={data.activity.n_trials} />
        <StatCard
          label="Goal rate"
          value={
            data.activity.goal_rate !== null
              ? `${Math.round(data.activity.goal_rate * 100)}%`
              : '—'
          }
        />
      </div>

      <h2>Datos</h2>
      <div className="table-wrap">
      <table>
        <tbody>
          <tr>
            <th>Email</th>
            <td>{u.email || '—'}</td>
          </tr>
          <tr>
            <th>Roles</th>
            <td>
              {u.roles.map((r) => (
                <span key={r} className="tag mr-1">
                  {r}
                </span>
              ))}
            </td>
          </tr>
          <tr>
            <th>Display name</th>
            <td>{data.profile?.display_name || '—'}</td>
          </tr>
          <tr>
            <th>Pie dominante</th>
            <td>{data.profile?.dominant_foot || '—'}</td>
          </tr>
          <tr>
            <th>Altura</th>
            <td>
              {data.profile?.height_cm ? `${data.profile.height_cm} cm` : '—'}
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <h2>Club</h2>
      <p className="text-sm text-text-muted">
        Club actual: <strong>{data.profile?.club?.name ?? 'Sin club'}</strong>. Para
        managers/técnicos, el staff se gestiona desde la página del club.
      </p>
      <div className="mb-lg flex flex-wrap items-center gap-sm">
        <select
          className="input max-w-xs"
          value={clubSelection}
          onChange={(e) => setClubSelection(e.target.value)}
          disabled={busy}
        >
          <option value="">— Sin club —</option>
          {clubs.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn-primary"
          disabled={busy || clubSelection === (data.profile?.club ? String(data.profile.club.id) : '')}
          onClick={() => void saveClub()}
        >
          Guardar club
        </button>
      </div>

      <h2>Research (piloto)</h2>
      <p className="text-sm text-text-muted">
        Consentimiento informado firmado en papel (protocolo §8) y screening de
        inclusión. Las sesiones <code>experimental</code>/<code>control</code> se
        bloquean sin un consentimiento vigente.
      </p>
      <div className="mb-sm flex flex-wrap gap-sm">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => void recordConsent(false)}
        >
          Registrar consentimiento
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => void recordConsent(true)}
        >
          Registrar consentimiento parental
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => void setInclusion(true)}
        >
          Marcar incluido en piloto
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => void setInclusion(false)}
        >
          Marcar excluido
        </button>
      </div>
      {consents.length === 0 && enrollment.length === 0 ? (
        <p className="mb-lg text-sm text-text-muted">
          Sin consentimientos ni screening registrados.
        </p>
      ) : (
        <div className="table-wrap mb-lg">
          <table>
            <tbody>
              {consents.map((c) => (
                <tr key={`c-${c.id}`}>
                  <th>Consentimiento</th>
                  <td>
                    <span className={c.revoked_at ? 'tag-err' : 'tag-ok'}>
                      {c.revoked_at ? 'revocado' : 'vigente'}
                    </span>{' '}
                    <span className="text-xs text-text-muted">
                      {c.consent_version}
                      {c.is_parental ? ' · parental' : ''} ·{' '}
                      {new Date(c.signed_at).toLocaleDateString('es-AR')}
                    </span>
                  </td>
                  <td className="text-right">
                    {!c.revoked_at && (
                      <button
                        type="button"
                        className="btn-ghost px-sm py-xs text-xs"
                        disabled={busy}
                        onClick={() => void revokeConsent(c.id)}
                      >
                        Revocar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {enrollment.map((e) => (
                <tr key={`e-${e.id}`}>
                  <th>Screening</th>
                  <td>
                    <span className={e.meets_inclusion ? 'tag-ok' : 'tag-err'}>
                      {e.meets_inclusion ? 'incluido' : 'excluido'}
                    </span>{' '}
                    <span className="text-xs text-text-muted">
                      {e.study_name}
                      {e.exclusion_reason ? ` · ${e.exclusion_reason}` : ''}
                    </span>
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Cuestionarios disponibles</h2>
      <p className="mb-sm text-sm text-text-muted">
        Por default todos los cuestionarios están disponibles. "Quitar acceso" lo
        oculta para este usuario sin borrar sus respuestas existentes.
      </p>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Cuestionario</th>
            <th>Respuestas globales</th>
            <th className="text-right">Acción</th>
          </tr>
        </thead>
        <tbody>
          {accessRows.map((c) => (
            <tr key={c.code}>
              <td>
                <strong>{c.code}</strong>{' '}
                <span className="text-xs text-text-muted">· {c.name_es}</span>
              </td>
              <td className="text-text-muted">{c.response_count}</td>
              <td className="text-right">
                <div className="flex justify-end gap-xs">
                  <button
                    type="button"
                    className="btn-ghost px-sm py-xs text-xs"
                    disabled={busy}
                    onClick={() => void toggleQuestionnaire(c.code, 'revoke')}
                  >
                    Quitar acceso
                  </button>
                  <button
                    type="button"
                    className="btn-ghost px-sm py-xs text-xs"
                    disabled={busy}
                    onClick={() => void toggleQuestionnaire(c.code, 'restore')}
                  >
                    Restaurar
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <h2>Sesiones</h2>
      {data.sessions.length === 0 ? (
        <p className="text-sm text-text-muted">Sin sesiones.</p>
      ) : (
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Protocolo</th>
              <th>Condición</th>
              <th>Estado</th>
              <th className="text-right">Ver</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.started_at
                    ? new Date(s.started_at).toLocaleString('es-AR', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })
                    : '—'}
                </td>
                <td>{s.protocol_id || '—'}</td>
                <td>{s.condition || '—'}</td>
                <td>
                  <span className={s.status === 'closed' ? 'tag-ok' : 'tag'}>
                    {s.status}
                  </span>
                </td>
                <td className="text-right">
                  <Link to={`/admin/sessions/${s.id}`}>detalle</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  )
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  )
}
